import { useEffect, useState } from 'react';
import {
  getDmResolutionCandidates,
  resolveDmException,
  type DmExceptionItem,
  type DmResolutionCandidate,
} from '@urbanik/api-client';
import { Button, CanonicalModal, SearchSelectField, TextAreaField } from '@urbanik/ui';
import { apiBase } from './api-base';

export function DmExceptionResolutionModal({
  item,
  onClose,
  onResolved,
}: {
  item: DmExceptionItem | null;
  onClose: () => void;
  onResolved: () => void;
}) {
  const [candidates, setCandidates] = useState<readonly DmResolutionCandidate[]>([]);
  const [assignmentId, setAssignmentId] = useState(''),
    [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false),
    [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!item) return;
    setLoading(true);
    setError('');
    setAssignmentId('');
    setReason('');
    void getDmResolutionCandidates(apiBase, item.id)
      .then(setCandidates)
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'No se pudieron consultar las titularidades.'),
      )
      .finally(() => setLoading(false));
  }, [item]);
  const save = async () => {
    if (!item || !assignmentId || reason.trim().length < 10) return;
    setSaving(true);
    setError('');
    try {
      await resolveDmException(apiBase, item.id, assignmentId, reason);
      onResolved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo resolver la excepción.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <CanonicalModal
      open={Boolean(item)}
      title="Asignar pago histórico"
      eyebrow="RESOLUCIÓN AUDITADA"
      description="Selecciona quién tenía la alícuota durante el período del pago. La fuente original permanecerá intacta."
      onClose={onClose}
    >
      {item ? (
        <div className="ui-form-stack">
          <div className="ui-alert" role="status">
            <strong>{item.memberLabel || 'Sin nombre en DM'}</strong>
            <p>
              Período {item.year}-{String(item.month).padStart(2, '0')} · Valor{' '}
              {item.amount ? `$${item.amount}` : item.rawValue}
            </p>
            <p>
              Origen: {item.sourceSheet}!{item.sourceCell}
            </p>
          </div>
          <SearchSelectField
            label="Titularidad válida en ese período"
            required
            loading={loading}
            value={assignmentId}
            onValueChange={setAssignmentId}
            placeholder="Buscar alícuota, nombre o cédula"
            emptyMessage="No existen titularidades con fechas válidas para este período."
            options={candidates.map((c) => ({
              value: c.assignmentId,
              label: `Alícuota ${c.slotNumber} · ${c.memberName}`,
              meta: `Cédula ${c.nationalId || 'no registrada'} · ${c.effectiveFrom} a ${c.effectiveTo ?? 'actual'}`,
            }))}
          />
          <TextAreaField
            label="Motivo y evidencia de la decisión"
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            hint="Mínimo 10 caracteres. Indica comprobante, documento o criterio revisado."
          />
          {error ? (
            <p className="ui-alert ui-alert--error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="ui-form-actions">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={() => void save()}
              disabled={saving || !assignmentId || reason.trim().length < 10}
            >
              {saving ? 'Guardando…' : 'Asignar y resolver'}
            </Button>
          </div>
        </div>
      ) : null}
    </CanonicalModal>
  );
}
